import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Users as UsersIcon, Plus, Pencil, Trash2, X, Shield, Wrench, LifeBuoy, KeyRound, SlidersHorizontal, Save, Building2 } from "lucide-react";
import Header from "@/components/Header";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/time";
import api, { apiError } from "@/lib/api";
import { useConfirm } from "@/components/ConfirmDialog";
import CustomerAdmin from "@/components/CustomerAdmin";
import { can, AREA_LABELS, LEVEL_LABELS, LEVEL_ORDER } from "@/lib/perms";

const ROLE_ICONS = { admin: Shield, supporter: LifeBuoy, techniker: Wrench };
const ROLE_CLS = {
  admin: "text-red-700 bg-red-500/15 border-red-500/30",
  supporter: "text-[#c00000] bg-[#c00000]/10 border-[#c00000]/30",
  techniker: "text-amber-700 bg-amber-500/15 border-amber-500/30",
};
const levelCls = (l) => l === "delete" ? "text-red-700" : l === "edit" ? "text-amber-700" : l === "view" ? "text-[#c00000]" : "text-gray-600";

export default function Users() {
  const { lang } = useLang();
  const de = lang === "de";
  const { user } = useAuth();
  const confirm = useConfirm();
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [roleData, setRoleData] = useState({ areas: [], levels: [], roles: [] });
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", username: "", password: "", role: "techniker", group: "", useOverride: false, overridePerms: {} });
  const [saving, setSaving] = useState(false);

  const canView = can(user, "benutzer", "view");
  const canEdit = can(user, "benutzer", "edit");
  const canDelete = can(user, "benutzer", "delete");
  const areaLbl = AREA_LABELS[de ? "de" : "en"];
  const lvlLbl = LEVEL_LABELS[de ? "de" : "en"];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ur, rr] = await Promise.all([api.get("/users"), api.get("/roles")]);
      setUsers(ur.data || []);
      setRoleData(rr.data || { areas: [], levels: [], roles: [] });
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { if (canView) load(); else setLoading(false); }, [load, canView]);

  const roleMeta = (id) => (roleData.roles || []).find((r) => r.id === id);
  const roleLabel = (id) => { const r = roleMeta(id); return r ? r.label : id; };

  const openNew = () => { setEditing(null); setForm({ name: "", email: "", username: "", password: "", role: "techniker", group: "", useOverride: false, overridePerms: {} }); setDlg(true); };
  const openEdit = (u) => {
    setEditing(u);
    const ov = u.permissions || {};
    setForm({ name: u.name || "", email: u.email, username: u.username || "", password: "", role: u.role || "techniker", group: u.group || "", useOverride: Object.keys(ov).length > 0, overridePerms: { ...ov } });
    setDlg(true);
  };

  const save = async () => {
    if (!editing && !form.username.trim() && (!form.email.trim() || !form.email.includes("@"))) { toast.error(de ? "E-Mail oder Benutzername erforderlich" : "Email or username required"); return; }
    if (!editing && form.password.length < 6) { toast.error(de ? "Passwort min. 6 Zeichen" : "Password min 6 chars"); return; }
    const permissions = form.useOverride ? Object.fromEntries(Object.entries(form.overridePerms).filter(([a, l]) => LEVEL_ORDER.includes(l))) : {};
    setSaving(true);
    try {
      if (editing) {
        const payload = { name: form.name, username: form.username.trim(), role: form.role, group: form.group, permissions };
        if (form.password) payload.password = form.password;
        await api.patch(`/users/${encodeURIComponent(editing.email)}`, payload);
        toast.success(de ? "Benutzer aktualisiert" : "User updated");
      } else {
        await api.post("/users", { name: form.name, email: form.email.trim().toLowerCase(), username: form.username.trim(), password: form.password, role: form.role, group: form.group, permissions });
        toast.success(de ? "Benutzer angelegt" : "User created");
      }
      setDlg(false); load();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  const del = async (u) => {
    const ok = await confirm({ title: de ? "Benutzer löschen?" : "Delete user?", message: u.email, confirmLabel: de ? "Löschen" : "Delete", danger: true });
    if (!ok) return;
    try { await api.delete(`/users/${encodeURIComponent(u.email)}`); toast.success(de ? "Gelöscht" : "Deleted"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const inputCls = "w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 placeholder-gray-500 focus:outline-none focus:border-[#c00000]/50";
  const labelCls = "block text-[11px] font-mono uppercase tracking-wider text-gray-600 mb-1.5";

  if (!canView) {
    return (
      <div className="min-h-screen bg-white text-gray-800">
        <Header />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center">
          <Shield className="w-10 h-10 text-gray-600 mx-auto mb-3" />
          <div className="text-lg text-gray-900 font-semibold">{de ? "Kein Zugriff" : "No access"}</div>
          <p className="text-sm text-gray-600 mt-1">{de ? "Dir fehlt die Berechtigung für die Benutzerverwaltung." : "You lack permission for user management."}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-gray-800">
      <Header />
      <div className="w-full px-4 sm:px-6 py-6">
        <div className="flex items-center gap-3 mb-1">
          <UsersIcon className="w-6 h-6 text-[#c00000]" />
          <h1 className="text-2xl font-bold text-gray-900">{de ? "Benutzer & Rechte" : "Users & permissions"}</h1>
        </div>
        <p className="text-sm text-gray-600 mb-5">{de ? "Konten verwalten, Rollen bearbeiten und Berechtigungen pro Bereich vergeben." : "Manage accounts, edit roles and assign per-area permissions."}</p>

        <div className="flex items-center gap-2 mb-5">
          <button data-testid="tab-users" onClick={() => setTab("users")} className={`flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-lg transition-colors ${tab === "users" ? "bg-[#c00000]/90 text-black" : "bg-gray-50 text-gray-700 hover:bg-gray-100"}`}><UsersIcon className="w-4 h-4" /> {de ? "Benutzer" : "Users"}</button>
          <button data-testid="tab-roles" onClick={() => setTab("roles")} className={`flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-lg transition-colors ${tab === "roles" ? "bg-[#c00000]/90 text-black" : "bg-gray-50 text-gray-700 hover:bg-gray-100"}`}><SlidersHorizontal className="w-4 h-4" /> {de ? "Rollen & Rechte" : "Roles & permissions"}</button>
          <button data-testid="tab-customers" onClick={() => setTab("customers")} className={`flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-lg transition-colors ${tab === "customers" ? "bg-[#c00000]/90 text-black" : "bg-gray-50 text-gray-700 hover:bg-gray-100"}`}><Building2 className="w-4 h-4" /> {de ? "Kunden-Portal" : "Customer portal"}</button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-gray-600"><Loader2 className="w-4 h-4 animate-spin" /> …</div>
        ) : tab === "users" ? (
          <>
            {canEdit && (
              <div className="flex justify-end mb-3">
                <button data-testid="user-new-btn" onClick={openNew} className="flex items-center gap-2 bg-[#c00000]/90 hover:bg-[#c00000] text-black font-semibold rounded-lg px-3 py-2 text-sm transition-colors"><Plus className="w-4 h-4" /> {de ? "Neuer Benutzer" : "New user"}</button>
              </div>
            )}
            <div className="space-y-2" data-testid="users-list">
              {users.map((u) => {
                const RIcon = ROLE_ICONS[u.role] || KeyRound;
                const cls = ROLE_CLS[u.role] || "text-slate-700 bg-slate-100 border-slate-200";
                const overrideCount = Object.keys(u.permissions || {}).length;
                return (
                  <div key={u.email} data-testid={`user-row-${u.email}`} className="flex items-center justify-between gap-3 bg-gray-50 border border-gray-200 rounded-xl p-3.5">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-gray-900 truncate">{u.name || u.email.split("@")[0]}</span>
                        <span className={`flex items-center gap-1 text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border ${cls}`}><RIcon className="w-3 h-3" /> {roleLabel(u.role)}</span>
                        {u.group && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-gray-200 bg-gray-50 text-gray-600">{u.group}</span>}
                        {overrideCount > 0 && <span data-testid={`user-override-${u.email}`} className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-amber-500/30 bg-amber-500/10 text-amber-700">{de ? `${overrideCount} Sonderrecht(e)` : `${overrideCount} override(s)`}</span>}
                      </div>
                      <div className="text-xs text-gray-600 font-mono truncate">{u.username ? <span className="text-[#c00000]">@{u.username}</span> : null}{u.username && !u.email.endsWith("@local") ? " · " : ""}{u.email.endsWith("@local") ? "" : u.email}{u.created_at ? ` · ${fmtDate(u.created_at)}` : ""}</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {canEdit && <button data-testid={`user-edit-${u.email}`} onClick={() => openEdit(u)} className="text-[11px] bg-gray-50 border border-gray-200 text-gray-600 rounded px-2 py-1.5 hover:text-gray-900 transition-colors"><Pencil className="w-3.5 h-3.5" /></button>}
                      {canDelete && <button data-testid={`user-delete-${u.email}`} onClick={() => del(u)} className="text-[11px] bg-gray-50 border border-gray-200 text-gray-600 rounded px-2 py-1.5 hover:text-red-700 hover:border-red-500/30 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : tab === "roles" ? (
          <RolesPanel roleData={roleData} de={de} canEdit={canEdit} canDelete={canDelete} areaLbl={areaLbl} lvlLbl={lvlLbl} onChanged={load} />
        ) : (
          <CustomerAdmin de={de} canDelete={canDelete} />
        )}
      </div>

      {dlg && (
        <>
          <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm" onClick={() => setDlg(false)} />
          <div data-testid="user-dialog" className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[70] w-[calc(100vw-2rem)] max-w-lg max-h-[90vh] overflow-y-auto bg-gray-50 border border-gray-200 rounded-2xl shadow-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">{editing ? (de ? "Benutzer bearbeiten" : "Edit user") : (de ? "Neuer Benutzer" : "New user")}</h3>
              <button data-testid="user-dialog-close" onClick={() => setDlg(false)} className="text-gray-600 hover:text-gray-900"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className={labelCls}>{de ? "Name" : "Name"}</label>
                <input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder={de ? "z. B. Max Mustermann" : "e.g. John Doe"} />
              </div>
              <div>
                <label className={labelCls}>{de ? "E-Mail (optional)" : "Email (optional)"}</label>
                {editing ? (
                  <div className="font-mono text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">{editing.email}</div>
                ) : (
                  <input data-testid="user-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} placeholder="name@firma.de" />
                )}
              </div>
              <div>
                <label className={labelCls}>{de ? "Benutzername (für Login)" : "Username (for login)"}</label>
                <input data-testid="user-username-input" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className={inputCls} placeholder={de ? "z. B. max.mustermann" : "e.g. john.doe"} />
                <p className="text-[11px] text-gray-600 mt-1">{de ? "Erlaubt: Buchstaben, Zahlen, . _ - — der Benutzer kann sich mit Benutzername ODER E-Mail anmelden." : "Login with username OR email."}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>{de ? "Rolle" : "Role"}</label>
                  <select data-testid="user-role-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls}>
                    {(roleData.roles || []).map((r) => <option key={r.id} value={r.id} className="bg-gray-50">{r.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>{de ? "Team (optional)" : "Team (optional)"}</label>
                  <input data-testid="user-group-input" value={form.group} onChange={(e) => setForm({ ...form, group: e.target.value })} className={inputCls} placeholder={de ? "z. B. Nord" : "e.g. North"} />
                </div>
              </div>
              <div>
                <label className={labelCls}>{editing ? (de ? "Neues Passwort (leer = unverändert)" : "New password (empty = unchanged)") : (de ? "Passwort" : "Password")}</label>
                <input data-testid="user-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} placeholder={de ? "min. 6 Zeichen" : "min 6 chars"} />
              </div>

              <div className="border-t border-gray-200 pt-3">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input data-testid="user-override-toggle" type="checkbox" checked={form.useOverride} onChange={(e) => setForm({ ...form, useOverride: e.target.checked })} className="accent-[#c00000] w-4 h-4" />
                  {de ? "Individuelle Rechte (überschreiben die Rolle)" : "Individual permissions (override the role)"}
                </label>
                {form.useOverride && (
                  <div className="mt-3 space-y-1.5" data-testid="user-override-matrix">
                    {(roleData.areas || []).map((a) => {
                      const roleLevel = (roleMeta(form.role)?.permissions || {})[a] || "none";
                      const cur = form.overridePerms[a] ?? "inherit";
                      return (
                        <div key={a} className="flex items-center justify-between gap-2">
                          <span className="text-xs text-gray-700">{areaLbl[a] || a}</span>
                          <select data-testid={`user-override-${a}`} value={cur} onChange={(e) => { const v = e.target.value; setForm((f) => { const op = { ...f.overridePerms }; if (v === "inherit") delete op[a]; else op[a] = v; return { ...f, overridePerms: op }; }); }}
                            className="bg-gray-50 border border-gray-200 rounded-lg px-2 py-1 text-xs text-gray-900 focus:outline-none focus:border-[#c00000]/50">
                            <option value="inherit" className="bg-gray-50">{de ? `Rolle (${lvlLbl[roleLevel]})` : `Role (${lvlLbl[roleLevel]})`}</option>
                            {LEVEL_ORDER.map((l) => <option key={l} value={l} className="bg-gray-50">{lvlLbl[l]}</option>)}
                          </select>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 mt-5">
              <button data-testid="user-cancel-btn" onClick={() => setDlg(false)} className="px-4 py-2 rounded-lg text-sm text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors">{de ? "Abbrechen" : "Cancel"}</button>
              <button data-testid="user-save-btn" onClick={save} disabled={saving} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-[#c00000]/90 hover:bg-[#c00000] text-black transition-colors disabled:opacity-50">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} {editing ? (de ? "Speichern" : "Save") : (de ? "Anlegen" : "Create")}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function RolesPanel({ roleData, de, canEdit, canDelete, areaLbl, lvlLbl, onChanged }) {
  const confirm = useConfirm();
  const [drafts, setDrafts] = useState({});   // roleId -> {label, permissions}
  const [savingId, setSavingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newRole, setNewRole] = useState({ label: "", permissions: {} });

  const areas = roleData.areas || [];
  const draftOf = (r) => drafts[r.id] || { label: r.label, permissions: { ...r.permissions } };
  const setDraft = (r, patch) => setDrafts((d) => ({ ...d, [r.id]: { ...draftOf(r), ...patch } }));
  const setDraftPerm = (r, area, level) => { const dr = draftOf(r); setDraft(r, { permissions: { ...dr.permissions, [area]: level } }); };

  const saveRole = async (r) => {
    const dr = draftOf(r);
    setSavingId(r.id);
    try { await api.patch(`/roles/${encodeURIComponent(r.id)}`, { label: dr.label, permissions: dr.permissions }); toast.success(de ? "Rolle gespeichert" : "Role saved"); setDrafts((d) => { const n = { ...d }; delete n[r.id]; return n; }); onChanged(); }
    catch (err) { toast.error(apiError(err)); } finally { setSavingId(null); }
  };
  const delRole = async (r) => {
    const ok = await confirm({ title: de ? "Rolle löschen?" : "Delete role?", message: r.label, confirmLabel: de ? "Löschen" : "Delete", danger: true });
    if (!ok) return;
    try { await api.delete(`/roles/${encodeURIComponent(r.id)}`); toast.success(de ? "Gelöscht" : "Deleted"); onChanged(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const createRole = async () => {
    if (!newRole.label.trim()) { toast.error(de ? "Name erforderlich" : "Name required"); return; }
    try { await api.post("/roles", { label: newRole.label.trim(), permissions: newRole.permissions }); toast.success(de ? "Rolle angelegt" : "Role created"); setCreating(false); setNewRole({ label: "", permissions: {} }); onChanged(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const LevelSelect = ({ value, disabled, onChange, testid }) => (
    <select data-testid={testid} disabled={disabled} value={value || "none"} onChange={(e) => onChange(e.target.value)}
      className={`w-full bg-gray-50 border border-gray-200 rounded-md px-2 py-1 text-xs focus:outline-none focus:border-[#c00000]/50 disabled:opacity-60 ${levelCls(value)}`}>
      {LEVEL_ORDER.map((l) => <option key={l} value={l} className="bg-gray-50 text-gray-900">{lvlLbl[l]}</option>)}
    </select>
  );

  return (
    <div className="space-y-3" data-testid="roles-panel">
      {canEdit && (
        <div className="flex justify-end">
          <button data-testid="role-new-btn" onClick={() => setCreating((v) => !v)} className="flex items-center gap-2 bg-[#c00000]/90 hover:bg-[#c00000] text-black font-semibold rounded-lg px-3 py-2 text-sm transition-colors"><Plus className="w-4 h-4" /> {de ? "Neue Rolle" : "New role"}</button>
        </div>
      )}

      {creating && (
        <div className="bg-gray-50 border border-[#c00000]/30 rounded-xl p-4" data-testid="role-create-card">
          <div className="flex items-center gap-2 mb-3">
            <KeyRound className="w-4 h-4 text-[#c00000]" />
            <input data-testid="role-new-name" value={newRole.label} onChange={(e) => setNewRole({ ...newRole, label: e.target.value })} placeholder={de ? "Rollenname (z. B. Logistik)" : "Role name"} className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none focus:border-[#c00000]/50" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {areas.map((a) => (
              <div key={a} className="flex items-center justify-between gap-2">
                <span className="text-xs text-gray-700">{areaLbl[a] || a}</span>
                <div className="w-32"><LevelSelect testid={`role-new-${a}`} value={newRole.permissions[a] || "none"} onChange={(v) => setNewRole((n) => ({ ...n, permissions: { ...n.permissions, [a]: v } }))} /></div>
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={() => setCreating(false)} className="px-3 py-1.5 rounded-lg text-sm text-gray-700 border border-gray-200 hover:bg-gray-50">{de ? "Abbrechen" : "Cancel"}</button>
            <button data-testid="role-create-save" onClick={createRole} className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold bg-[#c00000]/90 hover:bg-[#c00000] text-black"><Save className="w-4 h-4" /> {de ? "Anlegen" : "Create"}</button>
          </div>
        </div>
      )}

      {(roleData.roles || []).map((r) => {
        const RIcon = ROLE_ICONS[r.id] || KeyRound;
        const isAdmin = r.id === "admin";
        const dr = draftOf(r);
        const dirty = !!drafts[r.id];
        const readOnly = isAdmin || !canEdit;
        return (
          <div key={r.id} data-testid={`role-card-${r.id}`} className="bg-gray-50 border border-gray-200 rounded-xl p-4">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-2 min-w-0">
                <span className={`flex items-center gap-1.5 text-xs font-mono uppercase px-2 py-1 rounded border ${ROLE_CLS[r.id] || "text-slate-700 bg-slate-100 border-slate-200"}`}><RIcon className="w-3.5 h-3.5" /> {r.builtin ? r.label : ""}</span>
                {r.builtin ? (
                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-gray-200 bg-gray-50 text-gray-600">{de ? "eingebaut" : "built-in"}</span>
                ) : (
                  <input data-testid={`role-label-${r.id}`} disabled={!canEdit} value={dr.label} onChange={(e) => setDraft(r, { label: e.target.value })} className="bg-gray-50 border border-gray-200 rounded-lg px-2 py-1 text-sm text-gray-900 focus:outline-none focus:border-[#c00000]/50 disabled:opacity-60" />
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {dirty && !readOnly && <button data-testid={`role-save-${r.id}`} onClick={() => saveRole(r)} disabled={savingId === r.id} className="flex items-center gap-1.5 text-xs font-semibold bg-[#c00000]/90 hover:bg-[#c00000] text-black rounded-lg px-2.5 py-1.5 disabled:opacity-50">{savingId === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} {de ? "Speichern" : "Save"}</button>}
                {!r.builtin && canDelete && <button data-testid={`role-delete-${r.id}`} onClick={() => delRole(r)} className="text-gray-600 hover:text-red-700 border border-gray-200 hover:border-red-500/30 rounded-lg px-2 py-1.5"><Trash2 className="w-3.5 h-3.5" /></button>}
              </div>
            </div>
            {isAdmin ? (
              <p className="text-xs text-gray-600">{de ? "Administratoren haben immer Vollzugriff auf alle Bereiche." : "Administrators always have full access."}</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-2">
                {areas.map((a) => (
                  <div key={a} className="flex items-center justify-between gap-2">
                    <span className="text-xs text-gray-700 truncate">{areaLbl[a] || a}</span>
                    <div className="w-32 shrink-0"><LevelSelect testid={`role-${r.id}-${a}`} disabled={readOnly} value={dr.permissions[a] || "none"} onChange={(v) => setDraftPerm(r, a, v)} /></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
